import { useCallback, useRef, useState, type DependencyList } from 'react';

export interface SyncedDraft<T> {
  /** The editable value: the source until the operator touches it. */
  draft: T;
  /**
   * The source as it was when the draft was last seeded — the "before" to diff
   * the draft against. Frozen alongside the draft while editing, so a field the
   * server changes mid-edit (a passphrase filling in from the store, a DHCP
   * lease renewing) is NOT mistaken for an operator change and written back.
   */
  baseline: T;
  /** Change the draft. Marks it edited, so a background refetch stops re-syncing. */
  edit: (next: T | ((prev: T) => T)) => void;
  /** Clear the edited flag — call in a save's onSuccess so the next refetch re-syncs. */
  commit: () => void;
  /** Discard edits and snap draft + baseline back to the current source. */
  reset: () => void;
}

/** A cheap content signature of the dependency list — the deps are primitives. */
function signature(deps: DependencyList): string {
  return deps
    .map((d) => (d === null ? ' n' : d === undefined ? ' u' : String(d)))
    .join('');
}

/**
 * Editable local state seeded from a value a live query keeps refetching.
 *
 * The device pages reload every 30s and every save invalidates them, so the
 * naive `useEffect(() => setState(source), [deps])` overwrites whatever the
 * operator is part-way through typing — the bug where a WiFi passphrase would
 * revert mid-edit, read as unchanged, and never submit. This adopts fresh values
 * from the source ONLY while the draft is pristine; the first `edit` locks both
 * the draft and its `baseline` until `commit` (call it in the mutation's
 * onSuccess), after which the next refetch re-syncs to the saved truth.
 *
 * Diff the draft against `baseline`, never the live source: while the form is
 * frozen the live source can move under it (a credential filling in from the
 * store, an address changing on a lease renewal), and diffing against the live
 * value would turn that drift into a spurious write of the stale field.
 *
 * The sync is a render-phase compare, not an effect, on purpose. An effect's
 * recorded deps advance even when it early-returns, so after a save the values
 * that changed while editing would never be re-adopted and the form would stay
 * "dirty". Here `syncedKey` is left untouched while editing, so once `commit`
 * clears the flag the very next render — the one the post-save refetch triggers —
 * sees the signature differ and re-seeds. React supports this
 * "adjust-state-during-render" pattern; the write is conditional, so it settles
 * in one extra render with no loop.
 *
 * `deps` are the source's own primitive fields, exactly what the replaced effect
 * listed — the pair re-seeds when any of them changes and the draft is pristine.
 */
export function useSyncedDraft<T>(source: T, deps: DependencyList): SyncedDraft<T> {
  const key = signature(deps);
  const [draft, setDraft] = useState(source);
  const [baseline, setBaseline] = useState(source);
  const [syncedKey, setSyncedKey] = useState(key);
  // The latest source, read by reset without widening its dependency list.
  const latest = useRef(source);
  latest.current = source;
  const edited = useRef(false);

  // Render-phase sync: adopt the fresh source whenever it changes and the draft
  // is pristine. Skipped while edited, and `syncedKey` is not advanced then, so
  // the post-commit render still sees the change and re-seeds.
  if (!edited.current && key !== syncedKey) {
    setDraft(source);
    setBaseline(source);
    setSyncedKey(key);
  }

  const edit = useCallback((next: T | ((prev: T) => T)) => {
    edited.current = true;
    setDraft(next as Parameters<typeof setDraft>[0]);
  }, []);

  const commit = useCallback(() => {
    edited.current = false;
  }, []);

  const reset = useCallback(() => {
    edited.current = false;
    setDraft(latest.current);
    setBaseline(latest.current);
  }, []);

  return { draft, baseline, edit, commit, reset };
}
